import Tooltip from 'components/@extended/Tooltip';
import { ExpandMore as ExpandMoreIcon } from 'components/icons';
import Big from 'big.js';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Separator } from 'components/ui/separator';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { ReactNode, useCallback } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { useSelector } from 'store';
import { FeatureFlag } from 'types/features';
import { InvoiceMatterCalculationLog, InvoiceMatterForm, InvoiceMatterSummary } from 'types/invoice-details';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { paymentCurrencyInputClassName } from 'utils/usePaymentCurrencyMode';
import GreyBox from '../../../components/GreyBox';
import { SECTION_PANEL } from '../../SectionCard';

/** A label on the left, its figure on the right. */
const ROW = 'flex items-center justify-between';

const INPUT_CLASS = 'bg-paper max-w-[120px]';

const LABEL = 'text-caption text-fg-secondary';

/** A grey total that opens onto the inputs adjusting it. */
const SummaryTotalsGrayBox = ({
    isInViewMode,
    header,
    inputComponent,
}: {
    isInViewMode: boolean;
    header: ReactNode;
    inputComponent: ReactNode;
}) => (
    <Collapsible className={cn('border-grey-50 rounded-md border', isInViewMode && 'pointer-events-none')}>
        <CollapsibleTrigger className="group bg-grey-50 focus-visible:ring-ring/50 flex min-h-10 w-full items-center rounded-md py-[5px] pr-2 pl-3 text-left font-semibold outline-none focus-visible:ring-3 data-[panel-open]:rounded-b-none">
            <span className="mr-2 flex grow items-center justify-between">{header}</span>
            <ExpandMoreIcon
                fontSize="inherit"
                stroke="currentColor"
                strokeWidth={2}
                className={cn(
                    'text-primary shrink-0 transition-transform group-data-[panel-open]:rotate-90',
                    isInViewMode ? 'opacity-20' : 'opacity-100'
                )}
            />
        </CollapsibleTrigger>
        <CollapsibleContent keepMounted className={SECTION_PANEL}>
            <div className="border-divider rounded-b-md border px-6 py-2 @max-[1186px]/invoice:px-4">
                {inputComponent}
            </div>
        </CollapsibleContent>
    </Collapsible>
);

const AdjustmentInput = ({
    isInViewMode,
    control,
    onSubmit,
    getValues,
    currency,
    fieldName,
    textLabel,
    amountTotalBeforeAdjustments,
    initialAdjustmentAmount,
    feesForCappedAssignments,
    tooltip,
    disabled,
    isPaymentCurrencyMode,
}: {
    isInViewMode: boolean;
    control: Control<InvoiceMatterForm>;
    onSubmit: () => void;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    textLabel: string;
    currency: string;
    fieldName:
        | 'adjustmentAmountForFees'
        | 'adjustmentAmountForExpensesWithoutVat'
        | 'adjustmentAmountForExpensesWithVat';
    amountTotalBeforeAdjustments?: number;
    initialAdjustmentAmount?: number;
    feesForCappedAssignments?: number;
    tooltip?: string;
    disabled?: boolean;
    isPaymentCurrencyMode?: boolean;
}) => (
    <div className="flex max-w-[364px] flex-col">
        <div className={ROW}>
            <span className={LABEL}>{textLabel}</span>
            <MoneyDisplay amount={amountTotalBeforeAdjustments ?? 0} currency={currency} />
        </div>
        {fieldName === 'adjustmentAmountForFees' ? (
            <FeatureFlagged action="show" flag={FeatureFlag.CASE_ASSIGNMENT_ENABLED}>
                <div className={cn(ROW, 'mt-1')}>
                    <span className={LABEL}>{t('invoices.details.fees-for-capped-assignments')}</span>
                    <MoneyDisplay amount={feesForCappedAssignments ?? 0} currency={currency} />
                </div>
            </FeatureFlagged>
        ) : null}
        <div className={cn(ROW, 'mt-1')}>
            <span className={LABEL}>{t('invoices.details.adjustment')}</span>
            <Tooltip text={tooltip}>
                <div className="flex flex-row items-center gap-[0.5ch]">
                    <DecimalInput
                        allowNegative
                        name={fieldName}
                        control={control}
                        disabled={isInViewMode || disabled}
                        noReadOnlyDivider
                        wrapperClassName={cn(INPUT_CLASS, isPaymentCurrencyMode && paymentCurrencyInputClassName)}
                        onBlur={() => {
                            const currentAmount = getValues(fieldName);
                            if (
                                (currentAmount || initialAdjustmentAmount) &&
                                Number(currentAmount) !== initialAdjustmentAmount
                            ) {
                                onSubmit();
                            }
                        }}
                        onKeyDown={(e: { key: string }) => {
                            if (e.key === 'Enter') {
                                onSubmit();
                            }
                        }}
                    />
                    {currency}
                </div>
            </Tooltip>
        </div>
    </div>
);

const AdjustmentHeader = ({
    title,
    totalAmount,
    currency,
}: {
    title: string;
    currency: string;
    totalAmount?: number;
}) => (
    <>
        <span className="text-grey-500 text-[12px] font-normal">{title}</span>
        <MoneyDisplay amount={totalAmount ?? 0} currency={currency} />
    </>
);

/** A discount rate above 100% is refused. */
export const validateDiscountRate = (value: unknown) =>
    typeof value === 'number' && value > 100 ? t('validation.cannot-exceed-pct', { max: '100' }) : true;

interface CaseDiscount {
    /** The case's calculation, in the payment currency when the invoice shows it. */
    log: InvoiceMatterCalculationLog['paymentCurrency'];
    discountRate: InvoiceMatterForm['discountRate'];
    discountAmount: InvoiceMatterForm['discountAmount'];
    areCostsDiscounted: InvoiceMatterForm['areCostsDiscounted'];
    totalBeforeDiscount: number;
    totalWithoutVat: number;
    handleOnDiscountAmountChange: (value: string | undefined) => void;
    handleOnDiscountPercentageChange: (value: string | undefined) => void;
}

/**
 * The case's discount: its rate and its amount, each kept in step with the other over the total it applies to (the
 * fees, or the fees and expenses with Include expenses), and the net total after it.
 */
export const useCaseDiscount = ({
    control,
    setValue,
    summaryData,
    isPaymentCurrencyMode,
}: {
    control: Control<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    summaryData: InvoiceMatterSummary;
    isPaymentCurrencyMode?: boolean;
}): CaseDiscount => {
    const { calculationLog } = summaryData;
    const log = isPaymentCurrencyMode ? calculationLog?.paymentCurrency : calculationLog;
    const isVatExemptExcludedFromDiscount = useIsFeatureEnabled(FeatureFlag.EXCLUDE_VAT_EXEMPT_FROM_DISCOUNT);

    const [discountRate, discountAmount, areCostsDiscounted] = useWatch({
        control,
        name: ['discountRate', 'discountAmount', 'areCostsDiscounted'],
    });
    const matterDisountAmount = new Big(log?.total?.matterDiscountAmount ?? 0).toFixed(2);
    const totalAfterMatterAdjustments = log?.total?.totalAfterMatterAdjustments ?? 0;
    const totalWithoutVat = totalAfterMatterAdjustments + Number(matterDisountAmount);
    const feesAfterMatterAdjustments = log?.fees?.totalAfterMatterAdjustments ?? 0;
    const expensesWithVatAfterMatterAdjustments = log?.expensesWithVat?.totalAfterMatterAdjustments ?? 0;
    const vatExemptExpensesAfterMatterAdjustments = log?.expensesWithoutVat?.totalAfterMatterAdjustments ?? 0;
    const expensesAfterMatterAdjustments = isVatExemptExcludedFromDiscount
        ? expensesWithVatAfterMatterAdjustments
        : expensesWithVatAfterMatterAdjustments + vatExemptExpensesAfterMatterAdjustments;
    const totalBeforeDiscount = areCostsDiscounted
        ? feesAfterMatterAdjustments + expensesAfterMatterAdjustments
        : feesAfterMatterAdjustments;

    const handleOnDiscountAmountChange = useCallback(
        (value: string | undefined) => {
            if (value && !isNaN(Number(value)) && totalBeforeDiscount) {
                const newDiscountAmount = Math.abs(Number(value));
                const newDiscountRate = (newDiscountAmount / totalBeforeDiscount) * 100;
                const roundingValue = Math.pow(10, 7);
                setValue('discountRate', Math.round(newDiscountRate * roundingValue) / roundingValue);
            } else {
                setValue('discountRate', 0);
            }
        },
        [setValue, totalBeforeDiscount]
    );

    const handleOnDiscountPercentageChange = useCallback(
        (value: string | undefined) => {
            if (value && !isNaN(Number(value)) && totalBeforeDiscount) {
                const newDiscountRate = Math.abs(Number(value));
                const newDiscountAmount = (totalBeforeDiscount * newDiscountRate) / -100;
                setValue('discountAmount', new Big(Math.round(newDiscountAmount * 100) / 100).toFixed(2));
            } else {
                setValue('discountAmount', '');
            }
        },
        [setValue, totalBeforeDiscount]
    );

    return {
        log,
        discountRate,
        discountAmount,
        areCostsDiscounted,
        totalBeforeDiscount,
        totalWithoutVat,
        handleOnDiscountAmountChange,
        handleOnDiscountPercentageChange,
    };
};

type Props = {
    control: Control<InvoiceMatterForm>;
    summaryData: InvoiceMatterSummary;
    onSubmit: () => void;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    isInViewMode: boolean;
    isPaymentCurrencyMode?: boolean;
    displayCurrency: string;
};

export const SummaryHeaderRightSide = ({
    control,
    summaryData,
    onSubmit,
    getValues,
    setValue,
    isInViewMode,
    isPaymentCurrencyMode,
    displayCurrency,
}: Props) => {
    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);
    const isVatExemptExcludedFromDiscount = useIsFeatureEnabled(FeatureFlag.EXCLUDE_VAT_EXEMPT_FROM_DISCOUNT);
    const {
        log,
        discountRate,
        discountAmount,
        areCostsDiscounted,
        totalBeforeDiscount,
        totalWithoutVat,
        handleOnDiscountAmountChange,
        handleOnDiscountPercentageChange,
    } = useCaseDiscount({ control, setValue, summaryData, isPaymentCurrencyMode });

    const feesForCappedAssignments = (summaryData.invoiceAssignments || []).reduce(
        (acc, item) => acc + (item.capAdjustment ?? 0),
        0
    );

    return (
        <div className="flex w-full flex-col gap-2">
            <SummaryTotalsGrayBox
                header={
                    <AdjustmentHeader
                        totalAmount={log?.fees?.totalAfterMatterAdjustments}
                        currency={displayCurrency}
                        title={t('invoices.details.fees-total')}
                    />
                }
                isInViewMode={isInViewMode}
                inputComponent={
                    <AdjustmentInput
                        isInViewMode={isInViewMode}
                        disabled={areButtonsDisabled}
                        currency={displayCurrency}
                        control={control}
                        onSubmit={onSubmit}
                        getValues={getValues}
                        fieldName="adjustmentAmountForFees"
                        textLabel={t('invoices.details.fees-before-adjustment')}
                        amountTotalBeforeAdjustments={log?.fees?.totalBeforeMatterAdjustments}
                        initialAdjustmentAmount={summaryData.adjustmentAmountForFees}
                        feesForCappedAssignments={feesForCappedAssignments}
                        isPaymentCurrencyMode={isPaymentCurrencyMode}
                    />
                }
            />
            <SummaryTotalsGrayBox
                header={
                    <AdjustmentHeader
                        totalAmount={log?.expensesWithVat?.totalAfterMatterAdjustments}
                        currency={displayCurrency}
                        title={t('invoices.details.exp-total-with-vat')}
                    />
                }
                isInViewMode={isInViewMode}
                inputComponent={
                    <AdjustmentInput
                        isInViewMode={isInViewMode}
                        disabled={areButtonsDisabled}
                        currency={displayCurrency}
                        control={control}
                        onSubmit={onSubmit}
                        getValues={getValues}
                        fieldName="adjustmentAmountForExpensesWithVat"
                        textLabel={t('invoices.details.with-vat-before-adjustment')}
                        amountTotalBeforeAdjustments={log?.expensesWithVat?.totalBeforeMatterAdjustments}
                        initialAdjustmentAmount={summaryData.adjustmentAmountForExpensesWithVat}
                        isPaymentCurrencyMode={isPaymentCurrencyMode}
                    />
                }
            />
            <SummaryTotalsGrayBox
                header={
                    <AdjustmentHeader
                        totalAmount={log?.expensesWithoutVat?.totalAfterMatterAdjustments}
                        currency={displayCurrency}
                        title={t('invoices.details.exp-total-wo-vat')}
                    />
                }
                isInViewMode={isInViewMode}
                inputComponent={
                    <AdjustmentInput
                        isInViewMode={isInViewMode || isVatExemptExcludedFromDiscount}
                        disabled={areButtonsDisabled}
                        currency={displayCurrency}
                        control={control}
                        onSubmit={onSubmit}
                        getValues={getValues}
                        fieldName="adjustmentAmountForExpensesWithoutVat"
                        textLabel={t('invoices.details.wo-vat-before-adjustment')}
                        amountTotalBeforeAdjustments={log?.expensesWithoutVat?.totalBeforeMatterAdjustments}
                        initialAdjustmentAmount={summaryData.adjustmentAmountForExpensesWithoutVat}
                        tooltip="invoices.details.vat-exempt-expenses-adjustment-tooltip"
                        isPaymentCurrencyMode={isPaymentCurrencyMode}
                    />
                }
            />
            <SummaryTotalsGrayBox
                header={
                    <>
                        <span className="text-grey-500 text-[12px] font-normal">{t('invoices.details.discount')}</span>
                        <span className={cn(ROW, 'gap-2')}>
                            {areCostsDiscounted ? (
                                <span className="text-grey-500 text-[12px]">{t('invoices.details.with-expenses')}</span>
                            ) : null}
                            {`${discountRate || 0}%`}{' '}
                            <Separator orientation="vertical" className="bg-divider h-3.5 data-vertical:self-center" />
                            <MoneyDisplay
                                amount={
                                    isPaymentCurrencyMode
                                        ? (log?.total?.matterDiscountAmount ?? 0)
                                        : discountAmount || 0
                                }
                                currency={displayCurrency}
                            />
                        </span>
                    </>
                }
                isInViewMode={isInViewMode}
                inputComponent={
                    <div className="flex max-w-[364px] flex-col">
                        <CheckboxInput
                            label="invoices.details.include-expenses"
                            control={control}
                            name="areCostsDiscounted"
                            disabled={isInViewMode || areButtonsDisabled}
                            onChange={onSubmit}
                        />
                        <div className={ROW}>
                            <span className={LABEL}>{t('invoices.details.total-before-discount')}</span>
                            <MoneyDisplay amount={totalBeforeDiscount} currency={displayCurrency} />
                        </div>
                        <div className={cn(ROW, 'mt-1')}>
                            <span className={LABEL}>{t('invoices.details.discount-pct')}</span>
                            <div className="flex w-[152px] flex-row items-center gap-[0.5ch]">
                                <PercentageInput
                                    allowNegative
                                    validate={validateDiscountRate}
                                    name="discountRate"
                                    decimalPlaces={7}
                                    control={control}
                                    disabled={isInViewMode || areButtonsDisabled}
                                    noReadOnlyDivider
                                    wrapperClassName={cn(
                                        INPUT_CLASS,
                                        isPaymentCurrencyMode && paymentCurrencyInputClassName
                                    )}
                                    onChange={(e) => handleOnDiscountPercentageChange(e.target.value)}
                                    onBlur={() => {
                                        const currentAmount = getValues('discountRate');
                                        if (
                                            (currentAmount || summaryData.discountRate) &&
                                            Number(currentAmount) !== summaryData.discountRate
                                        ) {
                                            onSubmit();
                                        }
                                    }}
                                    onKeyDown={(e: { key: string }) => {
                                        if (e.key === 'Enter') {
                                            onSubmit();
                                        }
                                    }}
                                />
                                {`%`}
                            </div>
                        </div>
                        <div className={cn(ROW, 'mt-1')}>
                            <span className={LABEL}>
                                {t('invoices.details.discount-currency', { currency: displayCurrency })}
                            </span>
                            <div className="flex flex-row items-center gap-[0.5ch]">
                                <DecimalInput
                                    onlyNegative
                                    name="discountAmount"
                                    control={control}
                                    disabled={isInViewMode || areButtonsDisabled}
                                    noReadOnlyDivider
                                    wrapperClassName={cn(
                                        INPUT_CLASS,
                                        isPaymentCurrencyMode && paymentCurrencyInputClassName
                                    )}
                                    onChange={(e) => handleOnDiscountAmountChange(e.target.value)}
                                    onBlur={() => {
                                        const currentAmount = getValues('discountAmount');
                                        const isCurrentAmountUndefined =
                                            `${currentAmount}` === '' || currentAmount === null;
                                        if (
                                            (!isCurrentAmountUndefined &&
                                                currentAmount !==
                                                    new Big(
                                                        Math.round(
                                                            (summaryData.calculationLog?.total?.matterDiscountAmount ??
                                                                0) * 100
                                                        ) / 100
                                                    ).toFixed()) ||
                                            (isCurrentAmountUndefined &&
                                                summaryData.calculationLog?.total?.matterDiscountAmount)
                                        ) {
                                            onSubmit();
                                        }
                                    }}
                                    onKeyDown={(e: { key: string }) => {
                                        if (e.key === 'Enter') {
                                            onSubmit();
                                        }
                                    }}
                                />
                                {displayCurrency}
                            </div>
                        </div>
                    </div>
                }
            />
            <GreyBox
                key="net-total"
                title={t('invoices.details.net-total')}
                value={totalWithoutVat}
                currency={displayCurrency}
                className="flex-row items-center justify-between"
            />
        </div>
    );
};
